// src/views/contasReceber/ContaReceberModal.jsx
import React, { useState, useEffect } from "react";
import contasContabeisService from "../../services/contasContabeisService";
import condominosService from "../../services/condominosService";
import unidadesService from "../../services/unidadesService";

const fmtDate = v => { if (!v || v === "") return ""; if (v.includes && v.includes("T")) return v.split("T")[0]; return v; };

const TP_RECEITA = [
  { value: "CotaCondominial",   label: "Cota Condominial" },
  { value: "Multa",             label: "Multa" },
  { value: "Acordo",            label: "Acordo / Parcelamento" },
  { value: "Locacao",           label: "Locação de Área" },
  { value: "Caucao",            label: "Caução" },
  { value: "ReceitaFinanceira", label: "Receita Financeira" },
  { value: "Ressarcimento",     label: "Ressarcimento" },
  { value: "Outro",             label: "Outro" },
];

const ORIGEM_INFO = {
  Cota:       { label: "Cobrança / Cota Condominial", recurso: "Cobranças" },
  Multa:      { label: "Multa de Ocorrência",         recurso: "Ocorrências" },
  Reserva:    { label: "Reserva de Área Comum",       recurso: "Reservas" },
  AguaGas:    { label: "Leitura de Consumo",          recurso: "Consumo" },
  Recorrente: { label: "Despesa Recorrente",          recurso: "Despesas Recorrentes" },
  Acordo:     { label: "Acordo de Renegociação",      recurso: "Renegociações" },
};

const EMPTY = {
  tpReceita: "Outro", descricao: "", nrDocumento: "", dtEmissao: "", dtCompetencia: "",
  vlrTotal: "", idCondomino: "", idUnidade: "", idContaContabil: "",
  incluirNoBoleto: 0, ehCaucao: 0, observacao: "", nrParcelas: "1",
  dtPrimeiroParcela: new Date().toISOString().split("T")[0],
};

function calcPreview(vlrTotal, nrParcelas, dtPrimeiro) {
  const parcelas = [];
  const vt = parseFloat(vlrTotal || 0);
  const np = parseInt(nrParcelas || 1);
  if (!vt || !dtPrimeiro) return [];
  const base = parseFloat((vt / np).toFixed(2));
  const diff = parseFloat((vt - base * np).toFixed(2));
  for (let i = 0; i < np; i++) {
    const dt = new Date(dtPrimeiro); dt.setMonth(dt.getMonth() + i);
    parcelas.push({
      nrParcela: i + 1,
      dtVencimento: dt.toISOString().split("T")[0],
      // Padronização: última parcela absorve a diferença
      vlrParcela: i === np - 1 ? parseFloat((base + diff).toFixed(2)) : base,
    });
  }
  return parcelas;
}

export default function ContaReceberModal({ show, onClose, onSave, editData, saving = false, idCondominio, condominioNome = "" }) {
  const isEdit = !!editData?.id;
  const isDuplicar = !!editData?._duplicar;
  const tpOrigem = editData?.tpOrigem || (isDuplicar ? "Avulso" : "Avulso");
  const origemNaoAvulsa = isEdit && tpOrigem !== "Avulso";
  const origemInfo = ORIGEM_INFO[tpOrigem];

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [contasContabeis, setContasContabeis] = useState([]);
  const [condominos, setCondominos] = useState([]);
  const [unidades, setUnidades] = useState([]);

  useEffect(() => {
    if (!idCondominio) return;
    contasContabeisService.listar({ idCondominio, situacao: "ativo", natureza: "Analitica" })
      .then(r => setContasContabeis(r.data || [])).catch(() => {});
    condominosService.listarSelect(idCondominio).then(r => setCondominos(r.data || [])).catch(() => {});
    unidadesService.listar({ idCondominio, limit: 200 }).then(r => setUnidades(r.data || [])).catch(() => {});
  }, [idCondominio]);

  useEffect(() => {
    if (!show) return;
    if (editData) {
      setForm({
        ...EMPTY, ...editData,
        dtEmissao: fmtDate(editData.dtEmissao),
        dtCompetencia: fmtDate(editData.dtCompetencia),
        dtPrimeiroParcela: editData.parcelas?.[0]?.dtVencimento ? fmtDate(editData.parcelas[0].dtVencimento)
                          : EMPTY.dtPrimeiroParcela,
        nrParcelas: editData.parcelas?.length || 1,
      });
    } else { setForm({ ...EMPTY }); }
    setErrors({});
  }, [show, editData]);

  const set = (f, v) => { setForm(p => ({ ...p, [f]: v })); setErrors(p => ({ ...p, [f]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.descricao.trim()) e.descricao = "Descrição obrigatória";
    if (!origemNaoAvulsa) {
      if (!form.vlrTotal || parseFloat(form.vlrTotal) <= 0) e.vlrTotal = "Valor deve ser maior que zero";
      if (!form.dtPrimeiroParcela) e.dtPrimeiroParcela = "Data do primeiro vencimento obrigatória";
      // Valida soma das parcelas
      const preview = calcPreview(form.vlrTotal, form.nrParcelas, form.dtPrimeiroParcela);
      const soma = preview.reduce((a, b) => a + parseFloat(b.vlrParcela), 0);
      if (Math.abs(soma - parseFloat(form.vlrTotal || 0)) > 0.01) {
        e.vlrTotal = "Soma das parcelas difere do total";
      }
    }
    setErrors(e); return Object.keys(e).length === 0;
  };

  const preview = calcPreview(form.vlrTotal, form.nrParcelas, form.dtPrimeiroParcela);
  const fmtMoeda = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.55)" }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex align-items-center gap-3">
              <div style={{ width: 38, height: 38, background: "rgba(255,255,255,.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="bi bi-arrow-down-circle" style={{ fontSize: 18, color: "#fff" }} />
              </div>
              <div>
                <h5 className="modal-title mb-0">
                  {isDuplicar ? "Duplicar Lançamento" : isEdit ? "Editar Lançamento" : "Novo Lançamento a Receber"}
                </h5>
                <small style={{ color: "rgba(255,255,255,.6)", fontSize: 12 }}>{condominioNome}</small>
              </div>
            </div>
            <button className="btn-close btn-close-white" onClick={onClose} />
          </div>

          <div className="modal-body">
            {/* Aviso quando a CR foi gerada por outro módulo */}
            {origemNaoAvulsa && (
              <div className="alert alert-warning d-flex align-items-start gap-3 mb-3" style={{ borderLeft: "4px solid #F59E0B" }}>
                <i className="bi bi-info-circle-fill" style={{ fontSize: 20, color: "#B45309", marginTop: 2 }} />
                <div style={{ fontSize: 13 }}>
                  <strong>Lançamento gerado por outro módulo</strong>
                  <div style={{ marginTop: 4, color: "#78350F" }}>
                    Este lançamento foi criado pelo módulo <strong>{origemInfo?.recurso}</strong> ({origemInfo?.label}).
                    Para alterar valores ou parcelas, edite no módulo de origem. Aqui você só pode atualizar
                    a descrição, observação e conta contábil.
                  </div>
                </div>
              </div>
            )}

            <div className="form-section">
              <div className="form-section-title"><i className="bi bi-file-text" /> Identificação</div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Tipo de Receita</label>
                  <select className="form-select" value={form.tpReceita}
                    onChange={e => set("tpReceita", e.target.value)} disabled={origemNaoAvulsa}>
                    {TP_RECEITA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="col-md-8">
                  <label className="form-label">Descrição <span className="required-mark">*</span></label>
                  <input className={`form-control ${errors.descricao ? "is-invalid" : ""}`}
                    value={form.descricao} onChange={e => set("descricao", e.target.value)}
                    placeholder="Descrição do lançamento" />
                  {errors.descricao && <div className="invalid-feedback">{errors.descricao}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Nº Documento</label>
                  <input className="form-control" value={form.nrDocumento}
                    onChange={e => set("nrDocumento", e.target.value)} disabled={origemNaoAvulsa} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Data Emissão</label>
                  <input type="date" className="form-control" value={form.dtEmissao}
                    onChange={e => set("dtEmissao", e.target.value)} disabled={origemNaoAvulsa} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Data Competência</label>
                  <input type="date" className="form-control" value={form.dtCompetencia}
                    onChange={e => set("dtCompetencia", e.target.value)} disabled={origemNaoAvulsa} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Conta Contábil</label>
                  <select className="form-select" value={form.idContaContabil} onChange={e => set("idContaContabil", e.target.value)}>
                    <option value="">— Sem vínculo —</option>
                    {contasContabeis
                      .filter(c => ["ReceitaOrdinaria", "ReceitaExtraordinaria", "Outro"].includes(c.tpConta))
                      .map(c => <option key={c.id} value={c.id}>{c.classificacaoContabil} — {c.descConta}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title"><i className="bi bi-person" /> Condômino / Unidade</div>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Condômino</label>
                  <select className="form-select" value={form.idCondomino}
                    onChange={e => set("idCondomino", e.target.value)} disabled={origemNaoAvulsa}>
                    <option value="">— Sem vínculo —</option>
                    {condominos.map(c => <option key={c.id} value={c.id}>{c.nmCompleto} — {c.cpf_cnpj}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Unidade</label>
                  <select className="form-select" value={form.idUnidade}
                    onChange={e => set("idUnidade", e.target.value)} disabled={origemNaoAvulsa}>
                    <option value="">— Sem vínculo —</option>
                    {unidades.map(u => <option key={u.id} value={u.id}>{u.bloco ? `Bl ${u.bloco} — ` : ""}Unidade {u.unidade}</option>)}
                  </select>
                </div>
                <div className="col-md-2 d-flex align-items-end pb-1">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="incluirNoBoleto"
                      checked={!!form.incluirNoBoleto && Number(form.ehCaucao) !== 1}
                      onChange={e => set("incluirNoBoleto", e.target.checked ? 1 : 0)}
                      disabled={origemNaoAvulsa || Number(form.ehCaucao) === 1} />
                    <label className="form-check-label" htmlFor="incluirNoBoleto" style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-600)" }}>
                      No Boleto
                    </label>
                  </div>
                </div>
                {Number(form.ehCaucao) === 1 && (
                  <div className="col-12">
                    <div style={{ fontSize: 11.5, color: "#9A3412", background: "#FED7AA", padding: "6px 10px", borderRadius: 6 }}>
                      <i className="bi bi-info-circle me-1" />Caução não entra no boleto automaticamente.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title"><i className="bi bi-cash-coin" /> Valores e Parcelas</div>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Valor Total (R$) <span className="required-mark">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text">R$</span>
                    <input className={`form-control ${errors.vlrTotal ? "is-invalid" : ""}`} type="number" step="0.01" min="0.01"
                      value={form.vlrTotal} onChange={e => set("vlrTotal", e.target.value)}
                      disabled={origemNaoAvulsa} />
                    {errors.vlrTotal && <div className="invalid-feedback">{errors.vlrTotal}</div>}
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Nº Parcelas</label>
                  <input className="form-control" type="number" min="1" max="120"
                    value={form.nrParcelas} onChange={e => set("nrParcelas", e.target.value)}
                    disabled={isEdit && !isDuplicar} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">1º Vencimento <span className="required-mark">*</span></label>
                  <input type="date" className={`form-control ${errors.dtPrimeiroParcela ? "is-invalid" : ""}`}
                    value={form.dtPrimeiroParcela} onChange={e => set("dtPrimeiroParcela", e.target.value)}
                    disabled={isEdit && !isDuplicar} />
                  {errors.dtPrimeiroParcela && <div className="invalid-feedback">{errors.dtPrimeiroParcela}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">Observações</label>
                  <textarea className="form-control" rows={2} value={form.observacao}
                    onChange={e => set("observacao", e.target.value)} />
                </div>
              </div>

              {/* Preview parcelas — só na criação e duplicar */}
              {(!isEdit || isDuplicar) && preview.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--primary)", marginBottom: 8 }}>
                    <i className="bi bi-calendar3 me-1" />Pré-visualização das Parcelas
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {preview.map(p => (
                      <div key={p.nrParcela} style={{ background: "var(--gray-50)", border: "1px solid var(--gray-200)", borderRadius: 6, padding: "6px 12px", fontSize: 12, minWidth: 120 }}>
                        <div style={{ fontWeight: 700, color: "var(--primary)" }}>{p.nrParcela}/{preview.length}</div>
                        <div style={{ color: "var(--gray-500)" }}>{new Date(p.dtVencimento).toLocaleDateString("pt-BR")}</div>
                        <div style={{ fontWeight: 600 }}>{fmtMoeda(p.vlrParcela)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer d-flex justify-content-between">
            <div style={{ fontSize: 12, color: "var(--gray-400)" }}>
              <i className="bi bi-asterisk me-1" style={{ color: "#ef4444", fontSize: 9 }} />
              Campos obrigatórios
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={onClose}>
                <i className="bi bi-x-circle" /> Cancelar
              </button>
              <button className="btn btn-primary" onClick={() => { if (validate()) onSave({ ...form, id: isDuplicar ? undefined : editData?.id }); }} disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
                  : <><i className="bi bi-check-circle" /> {isEdit && !isDuplicar ? "Salvar" : "Lançar"}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
